import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge, PageHeader, formatDate, type BadgeTone } from '@/kit/ui'
import { compareVersions, productShort } from '@/lib/batch'
import { api, apiMode } from '@/lib/hubApi'
import { useCodeStore } from '@/store/codeStore'
import { useHubStore } from '@/store/hubStore'
import { useSettingsStore } from '@/store/settingsStore'
import type { Batch } from '@/types'

const TONE: Record<Batch['status'], BadgeTone> = { draft: 'warning', tests_passed: 'info', built: 'primary', live: 'success', previous: 'gray', retired: 'gray' }

/** GET /admin/v1/storage (LH-B2): what the Hub really has, per bundle and per part. */
interface Storage {
  bundles: { lender: string; product?: string | null; version: string; key: string; checksum: string; builtAt: string; builtBy: string; status: Batch['status']; image: string | null; digest: string | null; gitTag: string | null; gitCommit: string; gitUrl: string | null }[]
  gitUrl: string
  storageUrl: string
  registry: string | null
  parts: { git: { kind: 'forgejo' | 'folder'; up: boolean }; storage: { kind: 's3' | 'folder' }; docker: { up: boolean; note: string | null }; playground: { runtime: 'docker' | 'local' | null }; runner?: { kind: 'docker' | 'local' | null; note: string | null } }
  /** the runner's containers (LH-B3) */
  containers?: { name: string; lender: string; version: string; env: 'uat' | 'production'; role: string; state: string; health: string | null; startedAt: string | null; lastHealthAt: string | null }[]
}

/** Code storage (F3-5, real in LH-B2; the runner in LH-B3): git container → Build (image + bundle) → registry and storage → the runner (G12, "like Vercel"). */
export function CodeStorageScreen() {
  const batches = useCodeStore((s) => s.batches)
  const lenders = useHubStore((s) => s.lenders)
  const settings = useSettingsStore((s) => s.settings)
  const [hub, setHub] = useState<Storage | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!apiMode()) return
    let stop = false
    api<Storage>('GET', '/storage').then((s) => !stop && setHub(s)).catch((err: Error) => !stop && setError(err.message))
    return () => { stop = true }
  }, [batches])
  const bundles = useMemo(() => batches.filter((b) => b.bundle).sort((a, b) => a.lenderSlug.localeCompare(b.lenderSlug) || compareVersions(b.version, a.version)), [batches])
  const example = batches.find((b) => b.lenderSlug === 'moneyview' && b.status === 'live') ?? bundles[0]
  const draft = batches.find((b) => b.lenderSlug === example?.lenderSlug && (b.status === 'draft' || b.status === 'tests_passed'))
  const lenderOff = (slug: string) => lenders.find((l) => l.slug === slug)?.status !== 'active'
  // BP-1: a product's batch and the whole-lender one can share a version number: matched on the product too
  const real = (b: Batch) => hub?.bundles.find((x) => x.lender === b.lenderSlug && (x.product ?? null) === (b.productSlug ?? null) && x.version === b.version)
  const up = (ok: boolean, label: string) => <Badge tone={ok ? 'success' : 'danger'}>{label}</Badge>

  const BOXES: [string, string, string][] = [
    ['1 · Git container', 'Forgejo on the internal network: one repo per lender (<lender>/batchApi), one branch per version, one tag per build. Save = one commit in the editor\'s name, pushed from the playground\'s sandbox.', draft ? `${draft.git.branch} · ${draft.git.commit}` : example ? `${example.git.tag ?? example.git.branch} · ${example.git.commit}` : '—'],
    ['2 · Build (server)', 'Admin only, one at a time, after every test is green: an image from hub-sandbox (npm install, npm start must answer /_hub/health), every test event again inside it, then the bundle with its checksum and the git tag.', example?.bundle ? `${example.testRun?.passed ?? 0} / ${example.testRun?.total ?? 0} · ${example.bundle.checksum.slice(0, 18)}…` : '—'],
    ['3 · Registry + storage', 'The image hub-batch-<lender>:<version> in our registry, by digest; the bundle and its manifest in hub-media (RustFS, private), never changed. What runs comes from here, never from git: if git is down, live endpoints keep working.', example?.bundle ? `s3://${example.bundle.key}` : '—'],
    ['4 · Runner', 'Make live → the version\'s container starts from its image (by digest) with the prod variables from the vault, /_hub/health is checked, then the route switches; the previous container is kept for roll-back (one click, no rebuild). UAT runs the newest built version.', example ? `live ${example.version}${batches.find((b) => b.lenderSlug === example.lenderSlug && b.status === 'previous') ? ` · kept ${batches.find((b) => b.lenderSlug === example.lenderSlug && b.status === 'previous')!.version}` : ''}` : '—'],
  ]

  return (
    <>
      <PageHeader title="Code storage" subtitle="Where the lender code lives and how it runs (G12, “like Vercel”)" />
      {apiMode() && (
        <section className="card mb-3 text-xs" data-testid="storage-parts">
          {error && <span className="text-red-600 dark:text-red-400">The Hub did not answer: {error}</span>}
          {!hub && !error && <span className="text-muted">Asking the Hub…</span>}
          {hub && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>{up(hub.parts.git.up, hub.parts.git.kind === 'forgejo' ? 'git container' : 'git folder')} <span className="mono text-2xs text-muted">{hub.gitUrl}</span></span>
              <span>{up(true, hub.parts.storage.kind === 's3' ? 'hub-media' : 'bundle folder')} <span className="mono text-2xs text-muted">{hub.storageUrl}</span></span>
              <span>{up(!!hub.registry, 'registry')} <span className="mono text-2xs text-muted">{hub.registry ?? 'none: images stay on this Docker'}</span></span>
              <span>{up(hub.parts.docker.up, 'Docker')} {hub.parts.docker.note && <span className="text-2xs text-muted">{hub.parts.docker.note}</span>}</span>
              <span>{up(hub.parts.playground.runtime !== null, `playground: ${hub.parts.playground.runtime ?? 'off'}`)}</span>
              {hub.parts.runner && <span>{up(hub.parts.runner.kind !== null, `runner: ${hub.parts.runner.kind ?? 'off'}`)} {hub.parts.runner.note && <span className="text-2xs text-muted">{hub.parts.runner.note}</span>}</span>}
            </div>
          )}
        </section>
      )}
      {apiMode() && hub?.containers && hub.containers.length > 0 && (
        <div className="card-flush mb-3 overflow-x-auto" data-testid="runner-containers">
          <table className="table">
            <thead><tr><th>Container</th><th>Lender · version</th><th>Env</th><th>Role</th><th>State</th><th>Health</th></tr></thead>
            <tbody>
              {hub.containers.map((c) => (
                <tr key={c.name}>
                  <td className="mono text-2xs">{c.name}</td>
                  <td className="mono">{c.lender} {c.version}</td>
                  <td>{c.env === 'production' ? 'prod' : 'UAT'}</td>
                  <td>{c.role.replace('_', ' ')}</td>
                  <td><Badge tone={c.state === 'running' ? 'success' : c.state === 'failed' ? 'danger' : 'gray'}>{c.state}</Badge></td>
                  <td className="text-2xs">{c.health ?? '—'}{c.lastHealthAt ? ` · ${formatDate(c.lastHealthAt)}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <section className="card">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {BOXES.map(([t, d, m]) => (
            <div key={t} className="flex flex-col gap-1.5 rounded-md border border-slate-200 p-3 dark:border-slate-700">
              <b className="text-xs">{t}</b>
              <span className="text-2xs text-muted">{d}</span>
              <span className="mt-auto font-mono text-2xs text-primary-text">{m}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-2xs text-muted">
          Every built version is kept (bundle, image and git tag), so any version can be made live again. Keys are never in an image, a bundle or git: each container gets the lender's variables from the vault as its .env. Each container: {settings.runner.memoryMb} MB, {settings.runner.deadlineMs / 1000} s per lender call (Settings).
        </p>
      </section>
      <div className="card-flush mt-3 overflow-x-auto">
        <table className="table">
          <thead><tr><th>Bundle</th><th>Lender · product</th><th>Checksum</th><th>Git tag · commit</th>{apiMode() && <th>Image</th>}<th>Built</th><th>State</th></tr></thead>
          <tbody>
            {bundles.map((b) => {
              const r = real(b)
              return (
                <tr key={b.id} data-testid={`bundle-${b.productSlug ?? b.lenderSlug}-${b.version}`}>
                  <td className="mono">{b.bundle!.key.replace('gw-bundles/', '')}</td>
                  <td><Link to={`/lenders/${b.lenderSlug}/api/batches`}>{lenders.find((l) => l.slug === b.lenderSlug)?.name ?? b.lenderSlug}</Link>{b.productSlug ? <span className="text-muted"> · {productShort(b.productSlug)}</span> : null}</td>
                  <td className="mono">{b.bundle!.checksum.slice(0, 18)}…</td>
                  <td className="mono">{r?.gitUrl ? <a href={r.gitUrl} target="_blank" rel="noreferrer" className="underline">{b.git.tag ?? b.git.branch}</a> : b.git.tag} · {b.git.commit}</td>
                  {apiMode() && <td className="mono text-2xs" title={r?.digest ?? ''}>{r?.image ?? '— (demo data, not built here)'}</td>}
                  <td>{formatDate(b.bundle!.builtAt)} · {b.bundle!.builtBy.split('@')[0]}</td>
                  <td><Badge tone={b.status === 'live' && lenderOff(b.lenderSlug) ? 'warning' : TONE[b.status]}>{b.status.replace('_', ' ')}{b.status === 'live' && lenderOff(b.lenderSlug) ? ' · lender off' : ''}</Badge></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}
